import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import salesforceApi from '../services/salesforceApi';
import { formatSpecialtyJobTitle } from '../utils/formatting';

const DESKTOP_CARDS_PER_VIEW = 3;
const TOTAL_FEATURED_JOBS = 12;

const CarouselSection = styled.section`
  padding: 2.5rem 1.5rem 3rem 1.5rem;
  border-radius: 12px;
  margin: 0;
`;

const CarouselContainer = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;


const CarouselWrapper = styled.div`
  position: relative;
  padding: 0 2.7rem;
  margin: 0 auto;
  max-width: ${props => props.$isMobile ? '600px' : '1100px'};
  box-sizing: border-box;
`;

const CarouselViewport = styled.div`
  overflow: hidden;
  border-radius: 10px;
  width: 100%;
  position: relative;
`;

const CarouselTrack = styled.div`
  display: flex;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  width: ${props => `${props.$slidesCount * 100}%`};
  margin: 0;
  padding: 0;
`;

const CarouselSlide = styled.div`
  flex: 0 0 ${props => `${100 / props.$slidesCount}%`};
  display: flex;
  justify-content: center;
  align-items: stretch;
  padding: 10px;
  box-sizing: border-box;
  background-color: transparent;
`;

const JobCard = styled.div`
  background-color: white;
  padding: 1.25rem;
  flex: 0 0 ${props => props.$isMobile ? '100%' : `calc(${100 / DESKTOP_CARDS_PER_VIEW}% - 20px)`};
  margin: 0 10px;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
  }
`;

const JobHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
  
  @media (max-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
    flex-wrap: wrap;
  }
`;  

const JobTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  font-size: 1.2rem;
  color: #2c3e50;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 70%;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 0.25rem;
    max-width: 65%;
  }
`;


const JobLocation = styled.div`
  font-size: 0.9rem;
  color: #28346c;
  margin-bottom: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const PayAndDateRow = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const JobPayRange = styled.div`
  font-size: 0.9rem;
  color: #28346c;
  font-weight: 500;
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const PayLabel = styled.span`
  font-size: 0.75rem;
  color: #7f8c8d;
  margin-right: 0.3rem;
  font-weight: normal;
`;

const JobPostedDate = styled.div`
  font-size: 0.8rem;
  color: #7f8c8d;
  text-align: right;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    margin-top: 0.1rem;
  }
`;

const ScrollToTopLink = styled(Link)`
  text-decoration: none;
  
  &:hover {
    text-decoration: none;
  }
`;

const ViewJobButton = styled(ScrollToTopLink)`
  display: inline-block;
  background-color: #28346c;
  color: white;
  padding: 0.6rem 1rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  text-align: center;
  margin-top: 1rem;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: all 0.6s ease;
  }
  
  &:hover {
    background-color: #1f2a50;
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(40,52,108,0.25);
    color: white;
  }
  
  &:hover::before {
    left: 100%;
  }
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
    padding: 0.5rem 0.9rem;
  }
`;

const CarouselButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  font-size: 1.1rem;
  color: #28346c;
  z-index: 2;
  transition: all 0.2s;
  
  &:hover {
    background-color: #28346c;
    color: white;
    transform: translateY(-50%) scale(1.05);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &.prev {
    left: 0;
  }
  
  &.next {
    right: 0;
  }
`;

const NavigationDots = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
`;

const NavigationDot = styled.button`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.$active ? '#28346c' : '#ddd'};
  margin: 0 5px;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background-color: ${props => props.$active ? '#28346c' : '#aaa'};
    transform: scale(1.2);
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.1rem;
  color: #7f8c8d;
`;

const LoadingDots = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
`;

const LoadingDot = styled.div`
  width: 1rem;
  height: 1rem;
  background-color: #f47424;
  border-radius: 50%;
  animation: bounce-pulse 1.4s infinite ease-in-out;
  animation-delay: ${props => props.$delay || '0s'};

  @keyframes bounce-pulse {
    0%, 80%, 100% {
      transform: scale(0.6);
      opacity: 0.6;
    }
    40% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 2rem;
  color: #e74c3c;
  border: 1px solid #e74c3c;
  border-radius: 8px;
  margin: 2rem auto;
  max-width: 80%;
  background-color: white;
`;

const chunkArray = (array, size) => {
  const chunkedArr = [];
  for (let i = 0; i < array.length; i += size) {
    chunkedArr.push(array.slice(i, i + size));
  }
  return chunkedArr;
};

const useMobileDetection = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    
    const handleMediaQueryChange = (event) => {
      setIsMobile(event.matches);
    };
    
    setIsMobile(mediaQuery.matches);
    
    mediaQuery.addEventListener('change', handleMediaQueryChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleMediaQueryChange);
    };
  }, []);
  
  return isMobile;
};

const formatDaysOpenText = (daysOpen) => {
  if (daysOpen === undefined || daysOpen === null) return '';
  
  const days = parseInt(daysOpen, 10);
  if (isNaN(days)) return '';
  
  if (days === 0) return 'Today';
  return days === 1 ? '1 day ago' : `${days} days ago`;
};

const FeaturedJobCarousel = () => {
  const [featuredJobs, setFeaturedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSlide, setActiveSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const carouselRef = useRef(null);
  const autoScrollIntervalRef = useRef(null);
  
  const isMobile = useMobileDetection();
  
  useEffect(() => {
    const fetchFeaturedJobs = async () => {
      try {
        setLoading(true);
        setError('');
        const allJobs = await salesforceApi.getJobs();
        
        const rnJobs = allJobs.filter(job => {
          const jobTitle = job.JobTitlen || job.VMS_Job_Title__c || job.AVTRRT__Job_Title__c || job.Job_Title__c || '';
          return jobTitle.includes('RN');
        });
        
        const sortedJobs = rnJobs.sort((a, b) => {
          const daysOpenA = parseInt(a.Days_Open__c, 10) || Number.MAX_SAFE_INTEGER;
          const daysOpenB = parseInt(b.Days_Open__c, 10) || Number.MAX_SAFE_INTEGER;
          return daysOpenA - daysOpenB;
        });
        
        let topJobs = sortedJobs.slice(0, TOTAL_FEATURED_JOBS);
        
        if (topJobs.length < TOTAL_FEATURED_JOBS) {
          const placeholdersNeeded = TOTAL_FEATURED_JOBS - topJobs.length;
          const placeholders = Array.from({ length: placeholdersNeeded }).map((_, i) => ({
            Record_Id: `placeholder-${i}`,
            JobTitlen: 'Coming Soon',
            isPlaceholder: true
          }));
          topJobs = [...topJobs, ...placeholders];
        }
        
        setFeaturedJobs(topJobs);
        setLoading(false);
      } catch (err) {
        setError('Failed to load featured jobs. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchFeaturedJobs();
  }, []);
  
  const cardsPerView = isMobile ? 1 : DESKTOP_CARDS_PER_VIEW;
  const groupedJobs = chunkArray(featuredJobs, cardsPerView);
  
  const totalSlides = groupedJobs.length;
  
  const memoizedGoToNextSlide = useCallback(() => {
    if (isAnimating || totalSlides === 0) return;
    
    setIsAnimating(true);
    setActiveSlide(prev => {
      return (prev + 1) % totalSlides;
    });
    
    setTimeout(() => {
      setIsAnimating(false);
    }, 600);
  }, [isAnimating, totalSlides]);
  
  useEffect(() => {
    const startAutoScroll = () => {
      autoScrollIntervalRef.current = setInterval(() => {
        if (!isPaused && totalSlides > 0) {
          memoizedGoToNextSlide();
        }
      }, 5000);
    };
    
    startAutoScroll();
    
    return () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
    };
  }, [isPaused, memoizedGoToNextSlide, totalSlides]);
  
  useEffect(() => {
    setActiveSlide(0);
  }, [isMobile]);
  
  const goToPrevSlide = () => {
    if (isAnimating || totalSlides === 0) return;
    
    setIsAnimating(true);
    setActiveSlide(prev => {
      return prev === 0 ? totalSlides - 1 : prev - 1;
    });
    
    setTimeout(() => {
      setIsAnimating(false);
    }, 600); // Match transition duration
  };
  
  const goToSlide = (index) => {
    if (isAnimating || totalSlides === 0) return;
    
    setIsAnimating(true);
    setActiveSlide(index);
    
    setTimeout(() => {
      setIsAnimating(false);
    }, 600); // Match transition duration
  };
  
  const handleMouseEnter = () => {
    setIsPaused(true);
  };
  
  const handleMouseLeave = () => {
    setIsPaused(false);
  };
  
  const renderJobCard = (job, isMobileView) => {
    if (job.isPlaceholder) {
      return (
        <JobCard key={job.Record_Id} $isMobile={isMobileView} style={{ opacity: 0.7 }}>
          <JobHeader>
            <JobTitle>{job.JobTitlen}</JobTitle>
          </JobHeader>
          <JobLocation>Check back soon</JobLocation>
          <div style={{ flex: 1 }}></div>
        </JobCard>
      );
    }
    
    return (
      <JobCard key={job.Record_Id} $isMobile={isMobileView}>
        <JobHeader>
          <JobTitle>
            {job.Specialityn && job.JobTitlen
              ? formatSpecialtyJobTitle(job.Specialityn, job.JobTitlen).trim() 
              : job.JobTitlen || job.Specialityn || 'Job Title Not Available'}
          </JobTitle>
          <JobPostedDate>
            {formatDaysOpenText(job.Days_Open__c)}
          </JobPostedDate>
        </JobHeader>
        
        {job.city && job.State && (
          <JobLocation>
            {job.city}, {job.State}
          </JobLocation>
        )}
        
        <PayAndDateRow>
          {(job.CalculatedPayRange || job.payRange) && (
            <JobPayRange>
              <PayLabel>WEEKLY PAY:</PayLabel>
              {(job.CalculatedPayRange || job.payRange).replace(/ weekly$/, '')}
            </JobPayRange>
          )}
        </PayAndDateRow>
        
        <ViewJobButton to={`/jobs/${job.Record_Id}`} onClick={() => window.scrollTo(0, 0)}>
          Apply Now
        </ViewJobButton>
      </JobCard>
    );
  
  };
  
  // Loading state
  if (loading) {
    return (
      <CarouselSection>
        <CarouselContainer>
          <LoadingState>
            <LoadingDots>
              <LoadingDot $delay="0s" />
              <LoadingDot $delay="0.2s" />
              <LoadingDot $delay="0.4s" />
            </LoadingDots>
          </LoadingState>
        </CarouselContainer>
      </CarouselSection>
    );
  }
  
  // Error state
  if (error) {
    return (
      <CarouselSection>
        <CarouselContainer>
          <ErrorState>{error}</ErrorState>
        </CarouselContainer>
      </CarouselSection>
    );
  }
  
  // No jobs found state
  if (featuredJobs.length === 0) {
    return (
      <CarouselSection>
        <CarouselContainer>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            No featured jobs available at this time.
          </div>
        </CarouselContainer>
      </CarouselSection>
    );
  }
  
  return (
    <CarouselSection>
      <CarouselContainer>
        <CarouselWrapper $isMobile={isMobile}>
          <CarouselButton
            className="prev"
            onClick={goToPrevSlide}
            aria-label="Previous job"
            disabled={isAnimating}
          >
            &#10094;
          </CarouselButton>
          
          <CarouselViewport
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            ref={carouselRef}
          >
            <CarouselTrack
              style={{
                transform: `translateX(-${activeSlide * (100 / totalSlides)}%)`
              }}
              $slidesCount={totalSlides}
            >
              {groupedJobs.map((jobGroup, slideIndex) => (
                <CarouselSlide key={`slide-${slideIndex}`} $slidesCount={totalSlides}>
                  {jobGroup.map(job => renderJobCard(job, isMobile))}
                  
                  {slideIndex === totalSlides - 1 && 
                   jobGroup.length < cardsPerView && 
                   Array.from({ length: cardsPerView - jobGroup.length }).map((_, i) => (
                     <JobCard 
                       key={`empty-${i}`} 
                       $isMobile={isMobile} 
                       style={{ visibility: 'hidden', opacity: 0 }}
                     />
                   ))}
                </CarouselSlide>
              ))}
            </CarouselTrack>
          </CarouselViewport>
          
          <CarouselButton
            className="next"
            onClick={memoizedGoToNextSlide}
            aria-label="Next job"
            disabled={isAnimating}
          >
            &#10095;
          </CarouselButton>
        </CarouselWrapper>
        
        <NavigationDots>
          {Array.from({ length: totalSlides }).map((_, index) => (
            <NavigationDot
              key={index}
              $active={activeSlide === index}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </NavigationDots>
      </CarouselContainer>
    </CarouselSection>
  );
};

export default FeaturedJobCarousel;